import { useEffect, useMemo, useState } from "react";

import { type Assignee, type SupportZendeskTicketModel, UserModel } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";

import { useAuthContext } from "../../Context/AuthContext";
import { useUserContext } from "../../Context/UserContext";
import { consoleErrorWithSentry } from "../../utils";

export type AssigneeUsers = Map<string, UserModel>;

type AssigneeUsersHookData = { assigneeUsers: AssigneeUsers | undefined; assigneeUsersLoading: boolean };

const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

function useAssigneeUsers(supportZendeskTickets?: SupportZendeskTicketModel[]): AssigneeUsersHookData {
  const [assigneeUsers, setAssigneeUsers] = useState<AssigneeUsers | undefined>(undefined);
  const [usersData, setUsersData] = useState<UserModel[]>([]);
  const [usersDataLoading, setUsersDataLoading] = useState(false);
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { userRoles } = useUserContext();

  const canFetchUsers = isDoitEmployee || userRoles?.supportRequester;

  const assigneeEmailChunks = useMemo(() => {
    if (!canFetchUsers) {
      return [];
    }

    const tickets = supportZendeskTickets ?? [];
    let assigneeEmails: string[] = [];

    if (tickets.length) {
      const allAssignees = tickets.map((zdt) => zdt.ticket.assignee?.email ?? "");
      const nonEmptyAssignees = allAssignees.filter((email) => email !== "");

      assigneeEmails = [...new Set(nonEmptyAssignees)];
    }

    return chunkArray(assigneeEmails, 30);
  }, [supportZendeskTickets, canFetchUsers]);

  useEffect(() => {
    if (!canFetchUsers) {
      setUsersData([]);
      setAssigneeUsers(new Map());
      setUsersDataLoading(false);
      return;
    }

    const fetchAllUsers = async () => {
      if (assigneeEmailChunks.length === 0) {
        setUsersData([]);
        return;
      }

      setUsersDataLoading(true);

      try {
        const allUsers: UserModel[] = [];

        for (const emailChunk of assigneeEmailChunks) {
          if (emailChunk.length === 0) continue;

          const query = getCollection(UserModel).where("email", "in", emailChunk);
          const snapshot = await query.get();

          snapshot.forEach((doc) => {
            const data = doc.data();
            allUsers.push(data as UserModel);
          });
        }

        setUsersData(allUsers);
      } catch (error) {
        consoleErrorWithSentry(error);
      } finally {
        setUsersDataLoading(false);
      }
    };

    fetchAllUsers();
  }, [assigneeEmailChunks, canFetchUsers]);

  const dataKey = useMemo(() => {
    if (!supportZendeskTickets?.length || !usersData?.length) {
      return "";
    }

    const emails = supportZendeskTickets
      .map((zdt) => zdt.ticket.assignee?.email ?? "")
      .filter((email) => usersData.some((user) => user.email === email))
      .sort()
      .join(",");

    const userEmails = usersData
      .map((user) => user.email)
      .sort()
      .join(",");

    return `${emails}:${userEmails}`;
  }, [supportZendeskTickets, usersData]);

  useEffect(() => {
    if (!supportZendeskTickets?.length || !usersData?.length) {
      setAssigneeUsers(new Map());
      return;
    }

    const assigneeUsersMap = new Map<string, UserModel>();
    const assignees: Assignee[] = supportZendeskTickets
      .map((zdt) => zdt.ticket.assignee)
      .filter((assignee): assignee is Assignee => assignee !== null);

    assignees.forEach((assignee) => {
      const user = usersData.find((user) => user.email === assignee.email);
      if (user) {
        assigneeUsersMap.set(assignee.email, user);
      }
    });

    setAssigneeUsers(assigneeUsersMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  return { assigneeUsers, assigneeUsersLoading: usersDataLoading };
}

export default useAssigneeUsers;
